import React from 'react';
import first from 'lodash.first';
import {
  withStateHandlers,
  withHandlers,
  withProps,
  compose,
  lifecycle,
} from 'recompose';
import styled from 'styled-components/macro';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { ellipsis } from 'polished';
import delve from 'dlv';

import Layout from 'components/styled/Layout/Layout';
import CompareBullet from 'components/styled/CompareBullet/CompareBullet';
import { CloseButton } from 'components/Header/EntityCompareHeader/CompareSearch/CompareSearch.style';
import CountryCompareSearch from './CountryCompareSearch/CountryCompareSearch';

import getEntityUrl from 'url-constructors/getEntityUrl';

import { ReactComponent as IconPlus } from 'icons/icon-plus.svg';
import { ReactComponent as IconClose } from 'icons/icon-close.svg';

import {
  Header,
  Title,
  CloseLink,
  ToggleButton,
} from 'components/Header/EntityCompareHeader/EntityCompareHeader';
import { loadCountries } from 'redux/reducers/countries';
import { ICountryFlagProps } from 'types/shared';
import { ThemeProps } from 'types/styled';
import { AllTopicTypes } from 'topics';
import { RootState } from 'redux/rootReducer';
import { EntityTypes } from 'context/entityContext';

export const Wrapper = styled('div')`
  margin-top: ${({ theme }): string => theme.spacing.xlarge};
  padding-bottom: 0.5em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.35);

  @media screen and (min-width: 30em) {
    padding-bottom: ${({ theme }): string => theme.spacing.large};
  }
`;

const List = styled('ul')`
  display: flex;
  flex-flow: row wrap;
  margin: -0.25em -0.5em;
`;

const ListItem = styled('li')`
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0.25em 0.5em;
  min-width: 0%;
  position: relative;

  svg {
    color: white;
  }

  @media screen and (min-width: 30em) {
    flex: 0 0 50%;
    max-width: 50%;
  }

  @media screen and (min-width: 60em) {
    flex: 0 0 25%;
    max-width: 25%;
  }
`;

export const CountryItem = styled('div')`
  display: flex;
  align-items: center;
  padding: ${({ theme }): string =>
    `${theme.spacing.xsmall} ${theme.spacing.small}`};
  background: rgba(0, 0, 0, 0.15);
  color: white;

  ${CloseButton} {
    flex-shrink: 0;
  }

  @media screen and (min-width: 30em) {
    padding: ${({ theme }): string => theme.spacing.small};
  }
`;

export const StyledCompareBullet = styled(CompareBullet)`
  margin-right: ${({ theme }): string => theme.spacing.small};
  margin-bottom: 0.125em;
`;

const Label = styled('span')`
  ${ellipsis('100%')};
  flex-grow: 1;
`;

const StyledToggleButton = styled(ToggleButton)<ThemeProps<'button'>>`
  padding: ${({ theme }): string =>
    `${theme.spacing.xsmall} ${theme.spacing.small}`};

  @media screen and (min-width: 30em) {
    padding: ${({ theme }): string => theme.spacing.small};
  }
`;

interface IOuterProps {
  /** One or more ids of entities from where we want to browse */
  entityIds: string[];
  /** The current EntityType */
  entityType: EntityTypes;
  /** The countryIds of the countries that are being compared */
  countryIds: string[];
  /** The current topic that is being browsed */
  topic: AllTopicTypes[] | AllTopicTypes;
  /** The EntitiesByID object from Redux in which the EntityMetadat is stored */
  entitiesById: {
    entity: any;
    error: boolean;
    loading: boolean;
    metadata: any;
  };
  history: any;
}

interface ICountryCompareHeaderProps extends IOuterProps {
  /** The countries that are being compared including the metadata */
  selectedCountries: ICountryFlagProps[];
  /** Does the header currently show a search bar? */
  isSearchVisible: boolean;
  /** Method to toggle the search bar */
  toggleSearchVisibility: Function;
  /** Method to remove a specific county */
  handleRemoveCountry: Function;
  /** Method to add a specific county */
  handleAddCountry: Function;
}

/** The Compare Header visible when comparing Countries */
const CountryCompareHeader: React.FC<ICountryCompareHeaderProps> = (props) => {
  const {
    entityIds,
    entityType,
    countryIds,
    selectedCountries,
    isSearchVisible,
    toggleSearchVisibility,
    handleRemoveCountry,
    handleAddCountry,
    topic,
    entitiesById,
  } = props;
  const topicString = Array.isArray(topic) ? topic.join('/') : topic;
  return (
    <Layout.MaxWidth>
      <Wrapper>
        <Header>
          <Title>Compare countries</Title>
          <CloseLink
            to={getEntityUrl({
              entityId: first(entityIds),
              entityType,
              topic: topicString,
              parentEntityId: delve(
                entitiesById,
                `${[first(entityIds)]}.entity.parent_group_id`
              ),
            })}
            preserveSearch={true}
          >
            Close compare
          </CloseLink>
        </Header>
        <List>
          {selectedCountries.map((country, index) => (
            <ListItem key={`compare-countries-item-${country.id}`}>
              <CountryItem>
                <StyledCompareBullet index={index} />
                <Label>{country.displayName}</Label>
                <CloseButton
                  onClick={(): void => handleRemoveCountry(country.id)}
                >
                  <IconClose />
                </CloseButton>
              </CountryItem>
            </ListItem>
          ))}
          {isSearchVisible && (
            <ListItem>
              <CountryCompareSearch
                index={selectedCountries.length}
                handleClose={toggleSearchVisibility}
                countryIds={countryIds}
                handleChange={({ target }: { target: any }): void => {
                  handleAddCountry(target.value);
                  toggleSearchVisibility();
                }}
              />
            </ListItem>
          )}
          {(!isSearchVisible || selectedCountries.length === 0) &&
            selectedCountries.length < 4 && (
              <ListItem>
                <StyledToggleButton
                  onClick={toggleSearchVisibility}
                  disabled={selectedCountries.length === 0}
                >
                  <IconPlus /> <Label>Add country</Label>
                </StyledToggleButton>
              </ListItem>
            )}
        </List>
      </Wrapper>
    </Layout.MaxWidth>
  );
};

const ComposedCountryCompareHeader = compose<
  ICountryCompareHeaderProps,
  IOuterProps
>(
  withRouter,
  connect(
    (state: RootState) => ({
      countries: state.countries.data,
    }),
    { loadCountries }
  ),
  lifecycle<any, any>({
    componentDidMount() {
      this.props.loadCountries();
    },
  }),
  withProps<any, any>(
    ({
      countries,
      countryIds,
    }: {
      countries: any[];
      countryIds: string[];
    }) => ({
      selectedCountries: countries
        .filter((country: any) => countryIds.includes(country.id))
        .sort(
          (a: any, b: any) =>
            countryIds.indexOf(a.id) - countryIds.indexOf(b.id)
        ),
    })
  ),
  withStateHandlers<any, any>(
    ({ countryIds }: { countryIds: string[] }) => ({
      /** Open the searchfield by default if there are less than 2 countries in compare */
      isSearchVisible: countryIds.length < 2,
    }),
    {
      toggleSearchVisibility: ({
        isSearchVisible,
      }: {
        isSearchVisible: boolean;
      }) => (): { isSearchVisible: boolean } => ({
        isSearchVisible: !isSearchVisible,
      }),
    }
  ),
  withHandlers<any, any>({
    handleAddCountry: ({
      countryIds,
      entityType,
      entityIds,
      topic,
      history,
      location,
    }: {
      countryIds: string[];
      entityType: EntityTypes;
      entityIds: string[];
      topic: AllTopicTypes[] | AllTopicTypes;
      history: any;
      location: any;
    }) => (countryId: string): void => {
      const topicString = Array.isArray(topic)
        ? (topic as any).join('/')
        : topic;

      history.push({
        ...location,
        pathname: `/compare/country/${[...countryIds, countryId].join(
          ','
        )}/entity/${entityType}/${entityIds.join(',')}/${topicString}`,
      });
    },
    handleRemoveCountry: ({
      countryIds,
      entityType,
      entityIds,
      topic,
      history,
      location,
      isSearchVisible,
      toggleSearchVisibility,
    }: {
      countryIds: string[];
      entityType: EntityTypes;
      entityIds: string[];
      topic: AllTopicTypes[] | AllTopicTypes;
      history: any;
      location: any;
      isSearchVisible: boolean;
      toggleSearchVisibility: Function;
    }) => (countryId: string): void => {
      /** Make sure we open the searchField when the last country (hence .length === 1) is removed */
      if (!isSearchVisible && countryIds.length === 1) {
        toggleSearchVisibility();
      }

      const topicString = Array.isArray(topic)
        ? (topic as any).join('/')
        : topic;

      history.push({
        ...location,
        pathname:
          countryIds.length > 1
            ? `/compare/country/${countryIds.filter(
                (loopId) => countryId !== loopId
              )}/entity/${entityType}/${entityIds.join(',')}/${topicString}`
            : `/compare/country/all/entity/${entityType}/${entityIds.join(
                ','
              )}/${topicString}`,
      });
    },
  })
)(CountryCompareHeader);

export default ComposedCountryCompareHeader;
